import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Helmet } from "react-helmet-async";
import { useDeleteBlogMutation, useGetAdminBlogsQuery } from "../../redux/api/blogsApi";
import { Pencil, Image, Trash2 } from "lucide-react";
import Loader from "../layout/Loader";
import AdminProfile from "../user/AdminProfile";

const ListBlogs = () => {
  const { data, isLoading, error } = useGetAdminBlogsQuery();
  const [deleteBlog, { isLoading: isDeleteLoading, error: deleteError, isSuccess }] = useDeleteBlogMutation();

  useEffect(() => {
    if (error) toast.error(error?.data?.message);
    if (deleteError) toast.error(deleteError?.data?.message);
    if (isSuccess) toast.success("Blog deleted successfully");
  }, [error, deleteError, isSuccess]);

  const deleteBlogHandler = (id) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      deleteBlog(id);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <AdminProfile>
      <Helmet>
        <title>All Blogs</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-6xl mx-auto mt-10 px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl sm:text-3xl font-playfair text-[#1c1917]">
            Blog List
          </h1>
          <Link
            to="/admin/blogs/new"
            className="bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair text-sm px-4 py-2 rounded-md shadow-sm transition"
          >
            + Create Blog
          </Link>
        </div>

        <div className="bg-[#f5f3ea] rounded-xl border border-[#e2e1d8] shadow-sm overflow-x-auto">
          {/* Table Header */}
          <div className="grid grid-cols-4 md:grid-cols-6 bg-white/80 border-b border-[#e2e1d8] text-[#1c1917] text-xs sm:text-sm font-playfair uppercase tracking-wide py-3 px-4">
            <div className="col-span-2">Title</div>
            <div>Category</div>
            <div className="hidden md:block">Author</div>
            <div className="hidden md:block">Created</div>
            <div className="text-right">Actions</div>
          </div>

          {/* Blog Rows */}
          <div className="divide-y divide-[#e2e1d8] font-sourcesans text-sm">
            {data?.blogs?.length > 0 ? (
              data.blogs.map((blog) => (
                <div
                  key={blog._id}
                  className="grid grid-cols-4 md:grid-cols-6 items-center py-4 px-4 hover:bg-white/60 transition gap-2"
                >
                  <div className="col-span-2 font-medium text-[#1c1917] truncate" title={blog.name}>
                    {blog.name}
                  </div>
                  <div className="text-stone-700 truncate" title={blog.category}>{blog.category}</div>
                  <div className="hidden md:block text-stone-600 truncate" title={blog.author}>{blog.author}</div>
                  <div className="hidden md:block text-xs text-stone-500">
                    {new Date(blog.createdAt).toLocaleDateString()}
                  </div>

                  {/* Actions: vertical on mobile, horizontal on md+ */}
                  <div className="flex flex-col md:flex-row justify-end gap-2">
                    <Link
                      to={`/admin/blogs/${blog._id}`}
                      className="p-1.5 border border-[#8a4b32]/50 rounded-md text-xs text-[#1c1917] hover:border-[#BC3F4A] hover:text-[#BC3F4A] transition text-center"
                      title="Edit"
                    >
                     <Pencil className="w-3.5 h-3.5" />
                    </Link>

                    <Link
                      to={`/admin/blogs/${blog._id}/upload_images`}
                      className="p-1.5 border border-green-600/40 rounded-md text-xs text-green-700 hover:border-green-700 hover:text-green-700 transition text-center"
                      title="Upload Images"
                    >
                     <Image className="w-3.5 h-3.5" />
                    </Link>

                    <button
                      onClick={() => deleteBlogHandler(blog._id)}
                      disabled={isDeleteLoading}
                      className="p-1.5 border border-[#BC3F4A]/60 rounded-md text-xs text-[#BC3F4A] hover:bg-[#BC3F4A] hover:text-white transition disabled:opacity-50 text-center"
                      title="Delete"
                    >
                     <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-stone-600">
                No blogs found.
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminProfile>
  );
};

export default ListBlogs;
